import { useState } from "react";
import { Link } from "react-router";
import { Button } from "../components/ui/Button";
import { Card } from "../components/ui/Card";
import { Input } from "../components/ui/Input";
import { Alert } from "../components/ui/Alert";
import { ThemeSwitcher } from "../components/theme/ThemeSwitcher";
import { AuthBrandingHeader } from "../components/auth/AuthBrandingHeader";
import { usePublicConfig } from "../hooks/usePublicConfig";
import { useBrandingSettings } from "../lib/settings/useBrandingSettings";

export const ForgotPasswordPage = () => {
  const { data: publicConfig } = usePublicConfig();
  const branding = useBrandingSettings();
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const res = await fetch("/api/auth/forgot-password", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setError(data?.message ?? "Something went wrong. Please try again.");
        return;
      }
      setSubmitted(true);
    } catch {
      setError("Network error. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="surface-grid flex min-h-screen items-center justify-center px-6 py-12">
      <div className="w-full max-w-md">
        <AuthBrandingHeader
          title="Reset your password"
          subtitle="We'll send you a link to get back in"
          authPages={publicConfig?.authPages}
          branding={branding.settings}
        />

        <Card className="glass-card">
          <div className="mb-5 flex items-center justify-between">
            <div>
              <h2 className="text-lg font-semibold">Forgot password</h2>
              <p className="text-sm text-muted-foreground">Enter your account email address.</p>
            </div>
            <ThemeSwitcher compact />
          </div>

          {submitted ? (
            <div className="space-y-4">
              <Alert variant="success">
                <div className="space-y-2">
                  <p className="font-medium">Check your email</p>
                  <p>If an account with that email exists, we've sent a password reset link. Check your inbox and spam folder.</p>
                  <p className="text-xs text-muted-foreground">The link expires in 1 hour.</p>
                </div>
              </Alert>
              <Link to="/login">
                <Button type="button" size="lg" className="w-full">
                  Back to login
                </Button>
              </Link>
            </div>
          ) : (
            <>
              {error && (
                <div className="mb-4 rounded-lg border border-destructive/40 bg-destructive/10 px-4 py-3 text-sm text-destructive-text">
                  {error}
                </div>
              )}

              <form className="space-y-4" onSubmit={handleSubmit}>
                <div className="space-y-1">
                  <label className="text-xs uppercase tracking-wide text-muted-foreground">Email address</label>
                  <Input
                    type="email"
                    placeholder="you@example.com"
                    value={email}
                    onChange={(event) => setEmail(event.target.value)}
                    autoComplete="email"
                    autoFocus
                    required
                  />
                </div>
                <Button type="submit" size="lg" className="w-full" disabled={loading}>
                  {loading ? "Sending…" : "Send reset link"}
                </Button>
              </form>
              <p className="mt-4 text-center text-xs text-muted-foreground">
                Remember your password? <Link to="/login">Sign in</Link>
              </p>
            </>
          )}
        </Card>
      </div>
    </div>
  );
};
